import { useAuth } from "src/context/AuthProvider";
import { useDashboard } from "./useDashboard";
import { isStandard, isAdmin as checkAdmin, canSeeBank as canSeeBankFor } from "src/utils/roles";
import { useApi } from "src/hooks/useApi";
import {
    HeroCard,
    PvsSavingsPill,
    FinApiPanel,
    OpenPostenCard,
    InvoiceTimelineChart,
    DunningPanel,
    ActivityCard,
    TodoCard,
} from "./DashboardPanels";
import { DashboardStatsPopup } from "./DashboardStatsPopup";
import "./dashboard.scss";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { PadUploadPopup } from "src/pages/Invoices/PadUploadPopup";
import type { Tag } from "src/types";
import { PadFlowStoryboardPopup } from "src/pages/Invoices/PadFlowStoryboardPopup";
import { GettingStartedPopup } from "src/components/OnboardingModal/OnboardingModal";
import Toast, { type ToastState } from "src/components/Toast/Toast";

import { TbFileEuro } from "react-icons/tb";
import { FaUserInjured } from "react-icons/fa6";
import { PiHandArrowDown } from "react-icons/pi";

// ── Dashboard section visibility ──────────────────────────────────────────────


function DashboardSkeleton() {
    return (
        <div className="dashboard">
            {/* Hero skeleton */}
            <div className="hero-card hero-card--skeleton">
                <div className="hero-card__main">
                    <div className="hero-card__left hero-skeleton__left">
                        <span className="skeleton--dark hero-skeleton__label" />
                        <span className="skeleton--dark hero-skeleton__title" />
                        <span className="skeleton--dark hero-skeleton__sub" />
                        <span className="skeleton--dark hero-skeleton__sub2" />
                        <span className="skeleton--dark hero-skeleton__cta" />
                    </div>
                    <div className="hero-card__right hero-skeleton__aside">
                    <div className="hero-card__aside">
                        <span className="skeleton--dark hero-skeleton__aside-label" />
                        <div className="hero-skeleton__stat-grid">
                            {[1, 2, 3].map((i) => (
                                <span key={i} className="skeleton--dark hero-skeleton__stat" />
                            ))}
                        </div>
                        <span className="skeleton--dark hero-skeleton__aside-label hero-skeleton__aside-label--second" />
                        <div className="hero-skeleton__stat-grid hero-skeleton__stat-grid--tight">
                            {[1, 2, 3].map((i) => (
                                <span key={i} className="skeleton--dark hero-skeleton__stat" />
                            ))}
                        </div>
                    </div>
                    </div>
                </div>
            </div>

            {/* Panel grid skeleton */}
            <div className="dashboard-grid">
                <div className="dashboard-grid__row">
                    {[1, 2].map((i) => (
                        <div key={i} className="panel skeleton-panel">
                            <span className="skeleton skeleton--block" />
                            <span className="skeleton skeleton--block skeleton--block-lg" />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

function DashboardBootLoader({ open }: { open: boolean }) {
    const { t } = useTranslation();
    const steps = [
        t("dashboard.boot.loading", "Loading your data…"),
        t("dashboard.boot.invoices", "Fetching invoices…"),
        t("dashboard.boot.patients", "Preparing patients list…"),
        t("dashboard.boot.calculating", "Calculating amounts…"),
    ];
    const [idx, setIdx] = useState(0);

    useEffect(() => {
        if (!open) return;
        setIdx(0);
        const id = window.setInterval(() => setIdx((n) => (n + 1) % steps.length), 1200);
        return () => window.clearInterval(id);
    }, [open, steps.length]);

    if (!open) return null;
    return (
        <div className="dash-boot" role="status" aria-live="polite">
            <div className="dash-boot__card">
                <div className="dash-boot__spinner" aria-hidden="true">
                    <span className="dash-boot__ring" />
                </div>
                <div className="dash-boot__title">{steps[idx]}</div>
                <div className="dash-boot__sub">{t("dashboard.boot.sub", "This can take a moment on slower networks.")}</div>
            </div>
        </div>
    );
}


export default function Dashboard() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [activityTick, setActivityTick] = useState(1);
    const [showUpload, setShowUpload] = useState(false);
    const [showFlowStoryboard, setShowFlowStoryboard] = useState(false);
    const [showGettingStarted, setShowGettingStarted] = useState(false);
    const [showStats, setShowStats] = useState(false);
    const [uploadToast, setUploadToast] = useState<ToastState | null>(null);

    useEffect(() => {
        const tick = () => {
            if (document.visibilityState === "visible") setActivityTick((n) => n + 1);
        };
        // Periodic refresh only while tab is visible
        const id = setInterval(tick, 60_000);
        // Immediately refresh when the user switches back to this tab
        document.addEventListener("visibilitychange", tick);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", tick);
        };
    }, []);
    const {
        loading,
        loadError,
        invoices,
        recipients,
        patientCount,
        unansweredQuestions,
        unansweredQuestionItems,
        questionnairePendingCount,
        totalOutstanding,
        pendingToSendCount,
        amountPendingToSend,
        amountSent,
        amountPaid,
        amountOverdue,
        amountM1,
        amountM2,
        amountM3,
        amountLegal,
        pieSent,
        overdueBreakdown,
        overdueTotalFines,
        pendingInvoices,
        openInvoiceCount,
        dunningCounts,
        dunningAmounts,
        yearlyData,
        yearMonthlyData,
    } = useDashboard(activityTick);

    const standardUser = isStandard(user?.doctor?.userRights);
    const isAdmin      = checkAdmin(user?.doctor?.userRights);
    // Doctor/Admin always see the full dashboard (with its own unconditional TodoCard below) —
    // this only matters for MedicalAssistant/Accounting, who default to the stripped-down
    // staff view and need it explicitly granted per team member (see TeamSettings.tsx).
    const canSeeDashboardTasks = user?.doctor?.permDashboardTasks ?? false;
    const canUpload    = user?.doctor?.permEdit !== undefined ? user.doctor.permEdit : !standardUser;
    const canSeeBank   = canSeeBankFor(user?.doctor);
    // Separate from canSeeBank: whether the Dashboard's compact bank-summary card
    // (FinApiPanel — total balance + a transaction preview) is also shown here, not just
    // reachable via the full Bank page. Same Doctor/Admin-by-default shape as permBank itself;
    // staff need it explicitly granted (see TeamSettings.tsx's "DASHBOARD" group).
    const canSeeBankDashboard = user?.doctor?.permBankDashboard !== undefined ? user.doctor.permBankDashboard : (user?.doctor?.userRights === 2 || user?.doctor?.userRights === 3);
    const { data: quotaData } = useApi<{ invoiceCount: number; invoiceAllowance: number | null; overageRate: number }>("/charges/quota");
    // Admin always sees it; a non-Admin needs permPvsSavings explicitly granted (see
    // TeamSettings.tsx — admin-only by default, unlike permDashboardTasks). Gated at the fetch
    // itself (unlike quotaData above), since this one runs the full computeChargesPeriod
    // aggregation server-side — not worth paying for on every dashboard load for staff who
    // can't see it anyway.
    const canSeePvsSavings = isAdmin || (user?.doctor?.permPvsSavings ?? false);
    const { data: pvsSavingsData } = useApi<{ savings: number; pvsCost: number } | null>(canSeePvsSavings ? "/charges/pvs-savings" : null);
    // Shared with Invoices/InvoiceRow's own gating — kept in sync so the todo count and the
    // row-level block/unblock state can never disagree on which invoices need consent.
    const { data: dashboardSettings } = useApi<{ requireLegalAcceptance?: boolean; tagsEnabled?: boolean }>("/settings");
    const requireLegalAcceptance = dashboardSettings?.requireLegalAcceptance !== false;

    // Same gating as Invoices.tsx's PadUploadPopup — without it, the upload popup opened from
    // here never gets tags at all, so the tag picker silently never shows even when tags are
    // available (it was simply never passed a `tags` prop, not that it was empty).
    const isPlus = (user?.praxis?.businessPlan ?? 1) >= 2;
    const tagsAvailable = (user?.praxis?.entitlements?.tagging ?? isPlus) && dashboardSettings?.tagsEnabled === true;
    const canViewTags = user?.doctor?.permTagsGroupsView !== undefined ? user.doctor.permTagsGroupsView : isAdmin;
    const showTagsUI = canViewTags && tagsAvailable;
    const { data: tagsData } = useApi<Tag[]>(showTagsUI ? "/tags" : null);
    const tags = tagsData ?? [];

    const bootOpen = loading && !invoices.length && !recipients.length;
    if (loading) {
        return (
            <>
                <DashboardSkeleton />
                <DashboardBootLoader open={bootOpen} />
            </>
        );
    }

    if (standardUser) {
        return (
            <div className="dashboard">
                {loadError && (
                    <div className="dash-load-alert" role="alert">
                        {loadError}
                    </div>
                )}
                <div className="staff-hero">
                    <div className="staff-hero__bg-circle staff-hero__bg-circle--1" />
                    <div className="staff-hero__bg-circle staff-hero__bg-circle--2" />
                    <div className="staff-hero__left">
                        <div className="staff-hero__greeting">{t("dashboard.welcomeBack")}</div>
                        <div className="staff-hero__name">
                            {user?.doctor?.title && <span className="staff-hero__title">{user.doctor.title} </span>}
                            {user?.doctor?.firstName} {user?.doctor?.lastName}
                        </div>
                        <div className="staff-hero__praxis">{user?.praxis?.name}</div>
                        {/* Staff never get HeroCard, so permPvsSavings needs its pill here too. */}
                        {canSeePvsSavings && pvsSavingsData?.savings != null && (
                            <PvsSavingsPill savings={pvsSavingsData.savings} canOpenCharges={false} />
                        )}
                        <div className="staff-hero__subtitle">{t("dashboard.staffSubtitle")}</div>
                    </div>
                    <div className="staff-hero__links">
                        {[
                            { label: t("nav.invoices"),    path: "/invoices",    accent: "#f59e0b", icon: <TbFileEuro /> },
                            { label: t("nav.patients"),    path: "/patients",    accent: "#6366f1", icon: <FaUserInjured /> },
                            { label: t("nav.recipients"),  path: "/recipients",  accent: "#0ea5e9", icon: <PiHandArrowDown /> },
                        ].map(({ label, path, accent, icon }) => (
                            <button
                                type="button"
                                key={path}
                                className="staff-hero__link"
                                onClick={() => navigate(path)}
                            >
                                <span className="staff-hero__link-icon" style={{ color: accent, background: accent + "22" }}>{icon}</span>
                                <span className="staff-hero__link-label">{label}</span>
                            </button>
                        ))}
                    </div>
                </div>
                <DunningPanel counts={dunningCounts} amounts={dunningAmounts} />
                {canSeeDashboardTasks && (
                    <TodoCard
                        invoices={invoices}
                        recipients={recipients}
                        unansweredQuestions={unansweredQuestions}
                        unansweredQuestionItems={unansweredQuestionItems}
                        questionnairePendingCount={questionnairePendingCount}
                        canSeeBank={canSeeBank}
                        requireLegalAcceptance={requireLegalAcceptance}
                    />
                )}
                {/* Staff are the actual target audience for permBankDashboard ("staff need it
                    explicitly granted") — this stripped-down view must still be able to show it,
                    independent of canSeeBank, same as the full Doctor/Admin view below. */}
                {canSeeBankDashboard && <FinApiPanel refreshTrigger={activityTick} canOpenBank={canSeeBank} canConnect={false} />}
            </div>
        );
    }

    return (
        <div className="dashboard">
            {loadError && (
                <div className="dash-load-alert" role="alert">
                    {loadError}
                </div>
            )}
            {uploadToast && <Toast message={uploadToast.message} variant={uploadToast.variant} onClose={() => setUploadToast(null)} />}
            {showUpload && (
                <PadUploadPopup
                    onClose={() => setShowUpload(false)}
                    onSuccess={(sendFailureCount) => {
                        setActivityTick(prev => prev + 1);
                        setUploadToast(
                            sendFailureCount && sendFailureCount > 0
                                ? { message: t("fileUpload.uploadSuccessWithFailures", { count: sendFailureCount }), variant: "error" }
                                : { message: t("fileUpload.uploadSuccess"), variant: "success" },
                        );
                    }}
                    tags={showTagsUI ? tags : undefined}
                />
            )}
            {showFlowStoryboard && (
                <PadFlowStoryboardPopup onClose={() => setShowFlowStoryboard(false)} />
            )}
            {showGettingStarted && (
                <GettingStartedPopup onClose={() => setShowGettingStarted(false)} />
            )}
            {showStats && (
                <DashboardStatsPopup
                    invoices={invoices}
                    pieSent={pieSent}
                    amountM1={amountM1}
                    amountM2={amountM2}
                    amountM3={amountM3}
                    amountLegal={amountLegal}
                    yearMonthlyData={yearMonthlyData}
                    onClose={() => setShowStats(false)}
                />
            )}
            <HeroCard
                title={user?.doctor?.title}
                firstName={user?.doctor?.firstName}
                lastName={user?.doctor?.lastName}
                praxisName={user?.praxis?.name}
                totalOutstanding={totalOutstanding}
                pendingToSendCount={pendingToSendCount}
                amountPendingToSend={amountPendingToSend}
                amountSent={amountSent}
                amountPaid={amountPaid}
                amountOverdue={amountOverdue}
                overdueBreakdown={overdueBreakdown}
                overdueTotalFines={overdueTotalFines}
                yearMonthlyData={yearMonthlyData}
                canUpload={canUpload}
                onUpload={() => setShowUpload(true)}
                onCreateInvoice={() => navigate("/invoices/new")}
                onOpenFlowStoryboard={() => setShowFlowStoryboard(true)}
                onOpenGettingStarted={() => setShowGettingStarted(true)}
                onOpenStats={() => setShowStats(true)}
                quotaCount={isAdmin && quotaData?.invoiceAllowance != null ? quotaData.invoiceCount : undefined}
                quotaAllowance={isAdmin && quotaData?.invoiceAllowance != null ? quotaData.invoiceAllowance : undefined}
                quotaOverageRate={isAdmin && quotaData?.invoiceAllowance != null ? quotaData.overageRate : undefined}
                pvsSavings={canSeePvsSavings ? pvsSavingsData?.savings : undefined}
                canOpenPvsSavings={isAdmin}
                showOverviewSections={true}
                patientCount={patientCount}
                totalInvoiceCount={invoices.length}
                openInvoiceCount={openInvoiceCount}
                dunningCounts={dunningCounts}
                dunningAmounts={dunningAmounts}
            />

            {(
                <div className="dashboard-grid__row dashboard-grid__row--equal dashboard-grid__row--activity-todo">
                    <ActivityCard refreshTrigger={activityTick} />
                    <TodoCard invoices={invoices} recipients={recipients} unansweredQuestions={unansweredQuestions} unansweredQuestionItems={unansweredQuestionItems} questionnairePendingCount={questionnairePendingCount} canSeeBank={canSeeBank} requireLegalAcceptance={requireLegalAcceptance} />
                </div>
            )}

            {/* FinApiPanel depends only on canSeeBankDashboard, independent of full Bank page
                access (canSeeBank) — a staff member can get just the Dashboard summary without
                ever being able to open the Bank page itself. OpenPostenCard is open invoices,
                unrelated to bank permissions, so it always renders regardless of either. */}
            <div className="dashboard-grid__row dashboard-grid__row--equal dashboard-grid__row--bank-pie">
                {canSeeBankDashboard && <FinApiPanel refreshTrigger={activityTick} />}
                <OpenPostenCard invoices={invoices} />
            </div>

        </div>
    );
}
